import { Button, Confirm, ConfirmInfo, IconBadge } from '@coop/components';
import { Info1Icon } from '@coop/icons';
import { useRef, useState } from 'react';

import cartRecipeSelectors from '../../../../cart/selectors/cartRecipeSelectors';
import type { RecipeCalculationType, RecipePrioritizationType } from '../../../../recipe/models';
import useModal from '../../../hooks/useModal';
import { useAppSelector } from '../../../hooks/useThunkDispatch';
import styles from './BuyableRecipe.module.scss';
import { getRecipeCalculationLabel } from './BuyableRecipeCalculationTypes.utils';

interface BuyableRecipeCalculationTypesProps {
    calculationType: RecipeCalculationType;
    prioritizationType?: RecipePrioritizationType | null;
    handleCalculationTypeChange: (
        calculationType: RecipeCalculationType,
        prioritization: RecipePrioritizationType | null,
    ) => void;
}

const BuyableRecipeCalculationTypes = (props: BuyableRecipeCalculationTypesProps) => {
    const { isOpen: isInfoOpen, open: openInfo, close: closeInfo } = useModal();

    const { isOpen: isConfirmOpen, open: openConfirm, close: closeConfirm } = useModal();

    const [targetCalculationType, setTargetCalculationType] = useState<{
        calculationType: RecipeCalculationType;
        prioritizationType: RecipePrioritizationType | null;
    }>({
        calculationType: props.calculationType,
        prioritizationType: props.prioritizationType || null,
    });

    const [selectedCalculationType, setSelectedCalculationType] = useState<{
        calculationType: RecipeCalculationType;
        prioritizationType: RecipePrioritizationType | null;
    }>({
        calculationType: props.calculationType,
        prioritizationType: props.prioritizationType || null,
    });

    const cartRecipesCalculationType = useAppSelector(
        cartRecipeSelectors.selectRecipesCalculationType,
    );

    const userConfirmedCalculationChoice = useRef(false);

    const handleCalculationTypeChange = (
        calculationType: RecipeCalculationType,
        prioritizationType: RecipePrioritizationType | null,
    ) => {
        setSelectedCalculationType({ calculationType, prioritizationType });
        props.handleCalculationTypeChange(calculationType, prioritizationType);
    };

    const handleButtonClick =
        (
            calculationType: RecipeCalculationType,
            prioritizationType: RecipePrioritizationType | null,
        ) =>
        () => {
            if (
                calculationType === selectedCalculationType.calculationType &&
                prioritizationType === selectedCalculationType.prioritizationType
            ) {
                // no change, do nothing
                return;
            }

            // user have seen the confirmation before, he/she now knows the drill, no need to shark him with confirm again.
            if (userConfirmedCalculationChoice.current) {
                handleCalculationTypeChange(calculationType, prioritizationType);
                return;
            }

            if (!cartRecipesCalculationType) {
                // if nothing in current cart, then its safe to change the type
                handleCalculationTypeChange(calculationType, prioritizationType);
                return;
            }

            if (
                cartRecipesCalculationType.calculationType !== calculationType ||
                cartRecipesCalculationType.prioritizationType !== prioritizationType
            ) {
                setTargetCalculationType({ calculationType, prioritizationType });
                openConfirm();
                return;
            }

            handleCalculationTypeChange(calculationType, prioritizationType);
        };

    return (
        <div className={styles.CalculationTypes}>
            <ul className={styles.CalculationTypesList} aria-label="Typ av varor">
                <li>
                    <Button
                        theme={
                            selectedCalculationType.calculationType === 'waste' &&
                            selectedCalculationType.prioritizationType !== 'organic'
                                ? 'primary'
                                : 'white'
                        }
                        aria-current={selectedCalculationType.calculationType === 'waste' && 'true'}
                        onClick={handleButtonClick('waste', null)}
                    >
                        Rekommenderat
                    </Button>
                </li>
                <li>
                    <Button
                        theme={
                            selectedCalculationType.calculationType === 'price'
                                ? 'primary'
                                : 'white'
                        }
                        aria-current={selectedCalculationType.calculationType === 'price' && 'true'}
                        onClick={handleButtonClick('price', null)}
                    >
                        Billigt
                    </Button>
                </li>
                <li>
                    <Button
                        theme={
                            selectedCalculationType.calculationType === 'waste' &&
                            selectedCalculationType.prioritizationType === 'organic'
                                ? 'primary'
                                : 'white'
                        }
                        aria-current={
                            selectedCalculationType.calculationType === 'waste' &&
                            selectedCalculationType.prioritizationType === 'organic' &&
                            'true'
                        }
                        onClick={handleButtonClick('waste', 'organic')}
                    >
                        Eko
                    </Button>
                    <ConfirmInfo
                        show={isInfoOpen}
                        heading="Välj typ av varor"
                        message={
                            <span>
                                <strong>Rekommenderat</strong> är varor som vi valt ut för att du
                                ska få minimalt med svinn. Du kan också välja{' '}
                                <strong>Billigt</strong> för bästa budgetval eller{' '}
                                <strong>Eko</strong> om du vill ha så många ekologiska alternativ
                                som möjligt.
                            </span>
                        }
                        buttonText="Jag förstår"
                        close={closeInfo}
                        renderTrigger={(ref) => (
                            <button
                                ref={ref}
                                type="button"
                                onClick={openInfo}
                                className="u-outlineSolidBase2"
                                aria-label="Info om hur vi rekommenderar varor"
                            >
                                <IconBadge
                                    icon={Info1Icon}
                                    size="medium"
                                    theme="white"
                                    aria-hidden="true"
                                />
                            </button>
                        )}
                    />
                </li>
            </ul>
            <Confirm
                onConfirm={() => {
                    handleCalculationTypeChange(
                        targetCalculationType.calculationType,
                        targetCalculationType.prioritizationType,
                    );
                    userConfirmedCalculationChoice.current = true;
                    closeConfirm();
                }}
                show={isConfirmOpen}
                heading={`Vill du att ${getRecipeCalculationLabel(targetCalculationType)} ska gälla för alla dina receptvaror?`}
                message="När du ändrar till en annan typ av varor, kan vi anpassa receptvarorna i din varukorg – även de du redan lagt till."
                close={closeConfirm}
            />
        </div>
    );
};

export default BuyableRecipeCalculationTypes;
